import type { HTMLProps, PropsWithChildren, ReactNode } from 'react';
import { useIntl } from 'react-intl';
import type { Address as AddressType } from '../../types';
import { AddressLabelPlaceholder, AddressPlaceholder } from '../../placeholders';
import { usePdfPreviewContext } from '../../pdf-preview-context.tsx';
import styles from './address.module.css';

interface AddressProps extends Omit<HTMLProps<HTMLDivElement>, 'label'> {
  label: ReactNode;
  address: AddressType;
  addressType: 'merchant' | 'client';
}

export function Address({
  label,
  address,
  addressType,
  children,
  ...props
}: PropsWithChildren<AddressProps>): ReactNode {
  const { formatDisplayName } = useIntl();
  const { variant } = usePdfPreviewContext();

  if (variant === 'DE') {
    const countryName = address.countryCode
      ? (formatDisplayName(address.countryCode, { type: 'region' }) ?? address.countryCode)
      : '';

    return (
      <address className={styles.address} {...props}>
        <AddressLabelPlaceholder addressType={addressType}>
          <div data-testid="pdf-preview-addresses-label">{label}</div>
        </AddressLabelPlaceholder>
        <AddressPlaceholder addressType={addressType}>
          <div className={styles.content}>
            <p className="body-2" data-testid="pdf-preview-addresses-street-address">
              {address.streetAddress}
            </p>
            {address.zipCode || address.city ? (
              <p className="body-2" data-testid="pdf-preview-addresses-city">
                {address.zipCode} {address.city}
              </p>
            ) : null}
            {countryName ? (
              <p className="body-2" data-testid="pdf-preview-addresses-country">
                {countryName}
              </p>
            ) : null}
            {children}
          </div>
        </AddressPlaceholder>
      </address>
    );
  }

  const countrySeparator = ', ';
  const showSeparator = (address.zipCode || address.city) && address.countryCode;

  return (
    <address className={styles.address} {...props}>
      <AddressLabelPlaceholder addressType={addressType}>
        <div data-testid="pdf-preview-addresses-label">{label}</div>
      </AddressLabelPlaceholder>
      <AddressPlaceholder addressType={addressType}>
        <div className={styles.content}>
          <p className="body-2" data-testid="pdf-preview-addresses-street-address">
            {address.streetAddress}
          </p>
          {address.zipCode || address.city || address.countryCode ? (
            <p className="body-2" data-testid="pdf-preview-addresses-city">
              {address.zipCode} {address.city}
              {showSeparator ? countrySeparator : null}
              {address.countryCode}
            </p>
          ) : null}
          {children}
        </div>
      </AddressPlaceholder>
    </address>
  );
}
